import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import * as api from '@/api/supervision.api';
import type { ExistenciaPorUbicacion, Ubicacion } from '@/api/tienda.api';
import { ModuleHeader } from '@/components/datos/ModuleHeader';
import { Superfiltro } from '@/components/datos/Superfiltro';
import { useFiltrosEnUrl } from '@/hooks/useFiltrosEnUrl';
import { sinFiltros, textoDeResultados, type Faceta } from '@/lib/superfiltro';
import { numero } from '@/lib/formato-tienda';

/**
 * EXISTENCIAS — lo que ve la vendedora (16-oct), de solo lectura.
 *
 * Primero su tienda, después el almacén y las otras tiendas, y el total. Así contesta «¿hay en
 * Santiago?» sin preguntarle a nadie. Contar y mover son de quien administra («Almacén y tiendas»).
 */

const etiquetaDe = (e: ExistenciaPorUbicacion) => `${e.producto.name} · ${e.label}`;
const hayEn = (e: ExistenciaPorUbicacion, u: Ubicacion) =>
  u.tipo === 'ALMACEN' ? Math.max(e.enAlmacen, 0) : Math.min(e.porTienda[u.id] ?? 0, e.total);

export default function Existencias() {
  // SUPERFILTRO (Josué, 14-oct): buscar, en qué lugar hay y si queda o no. En la URL.
  const { filtros, poner, ponerVarios } = useFiltrosEnUrl(
    { buscar: '', lugar: '', existencia: '' },
    { existencia: ['con', 'agotada'] },
  );
  const { data, isLoading, isError } = useQuery({ queryKey: ['existencias-para-ver'], queryFn: api.existenciasParaVer });

  // Su tienda primero; después el almacén y las demás, en el orden de siempre.
  const columnas = useMemo(() => {
    const lugares = data?.lugares ?? [];
    const suya = lugares.find((u) => u.id === data?.miTienda);
    return suya ? [suya, ...lugares.filter((u) => u.id !== suya.id)] : lugares;
  }, [data]);
  const todas = useMemo(() => data?.piezas ?? [], [data]);
  const filas = useMemo(() => {
    const q = filtros.buscar.trim().toLowerCase();
    const lugar = columnas.find((u) => u.id === filtros.lugar);
    return todas.filter(
      (e) =>
        (!q || `${etiquetaDe(e)} ${e.sku ?? ''} ${e.producto.sku ?? ''}`.toLowerCase().includes(q)) &&
        (!lugar || hayEn(e, lugar) > 0) &&
        (filtros.existencia !== 'con' || e.total > 0) &&
        (filtros.existencia !== 'agotada' || e.total <= 0),
    );
  }, [todas, columnas, filtros.buscar, filtros.lugar, filtros.existencia]);
  const facetas: Faceta[] = [
    {
      clave: 'lugar',
      titulo: 'Hay en',
      todas: 'Cualquier lugar',
      opciones: columnas.map((u) => ({ valor: u.id, etiqueta: u.nombre, cuenta: todas.filter((e) => hayEn(e, u) > 0).length })),
    },
    {
      clave: 'existencia',
      titulo: 'Existencia',
      todas: 'Todas',
      opciones: [
        { valor: 'con', etiqueta: 'Con existencia', cuenta: todas.filter((e) => e.total > 0).length },
        { valor: 'agotada', etiqueta: 'Agotadas', cuenta: todas.filter((e) => e.total <= 0).length },
      ],
    },
  ];
  const suya = columnas[0]?.id === data?.miTienda ? columnas[0] : undefined;

  return (
    <div data-existencias className="space-y-5">
      <ModuleHeader
        titulo="Existencias"
        subtitulo={
          suya
            ? `Lo que hay en ${suya.nombre} primero, y al lado el almacén y las otras tiendas.`
            : 'Lo que hay en el almacén y en cada tienda.'
        }
      />
      <section className="rounded-2xl border border-surface-border bg-surface">
        <div className="border-b border-surface-border p-4">
          <Superfiltro
            buscar={filtros.buscar}
            onBuscar={(v) => poner('buscar', v)}
            placeholder="Busca una pieza o su código…"
            facetas={facetas}
            valores={filtros}
            onCambiar={ponerVarios}
            onLimpiar={() => ponerVarios(sinFiltros(facetas))}
            resultados={isLoading ? undefined : textoDeResultados(filas.length, todas.length, ['pieza', 'piezas'])}
          />
        </div>
        {filas.length === 0 ? (
          <p className="p-6 text-sm text-slate-500">
            {isLoading
              ? 'Cargando…'
              : isError
                ? 'No se pudieron cargar las existencias. Recarga la página.'
                : todas.length > 0
                  ? 'Nada con esos filtros.'
                  : 'Todavía no hay productos cargados.'}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[32rem] text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
                  <th className="px-4 py-2 font-medium">Pieza</th>
                  {columnas.map((u) => (
                    <th
                      key={u.id}
                      className={`px-4 py-2 text-right font-medium ${u.id === suya?.id ? 'text-slate-900 dark:text-slate-100' : ''}`}
                    >
                      {u.id === suya?.id ? `${u.nombre} (la tuya)` : u.nombre}
                    </th>
                  ))}
                  <th className="px-4 py-2 text-right font-medium">Total</th>
                </tr>
              </thead>
              <tbody>
                {filas.map((e) => (
                  <tr key={e.id} className="border-t border-surface-border">
                    <td className="px-4 py-2 text-slate-900 dark:text-slate-100">{etiquetaDe(e)}</td>
                    {columnas.map((u) => (
                      <td
                        key={u.id}
                        className={`px-4 py-2 text-right tabular-nums ${u.id === suya?.id ? 'font-semibold text-slate-900 dark:text-slate-100' : 'text-slate-600 dark:text-slate-300'}`}
                      >
                        {numero(hayEn(e, u))}
                      </td>
                    ))}
                    <td className="px-4 py-2 text-right tabular-nums text-slate-500">{numero(e.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
